import React, { useState, useEffect, useCallback } from 'react';
import { formFactorLabel } from '../../services/hardwareApi';
import {
  Monitor,
  Copy,
  Check,
  Clock,
  Wifi,
  LogIn,
  LogOut,
  Activity,
  MapPin,
  Hash,
  Key,
  Calendar,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { terminalDisplayStatus, type DisplayTone } from './terminalDisplayStatus';
import { RubyLoader } from '../RubyLoader';
import { usableTerminalCode } from './TerminalNewCodeModal';

const ICON_BG: Record<DisplayTone, string> = { emerald: 'bg-emerald-100', amber: 'bg-amber-100', red: 'bg-red-100', stone: 'bg-stone-100' };
const ICON_TEXT: Record<DisplayTone, string> = { emerald: 'text-emerald-600', amber: 'text-amber-600', red: 'text-red-600', stone: 'text-stone-500' };
const TITLE_TEXT: Record<DisplayTone, string> = { emerald: 'text-emerald-700', amber: 'text-amber-700', red: 'text-red-700', stone: 'text-stone-600' };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Terminal {
  terminalId: string;
  provisionCode: string;
  /** true when provisionCode is the masked display form (RT-****-AB12). */
  provisionCodeMasked?: boolean;
  codeExpired?: boolean;
  locationId: string;
  locationName: string;
  registerName: string;
  terminalType: 'pos' | 'kiosk' | 'customer_display' | 'android_dual_screen' | 'online_sales';
  status: 'pending' | 'activated';
  createdAt: number;
  expiresAt?: number;
  activatedAt?: number;
  lastSeenAt?: number;
  /** The REGISTER row's status; null when the terminal was never claimed. */
  registerStatus?: string | null;
}

interface TerminalEvent {
  type: 'login' | 'logout';
  employeeId: string;
  employeeName: string;
  timestamp: number;
}

interface Props {
  terminal: Terminal;
  isOnline: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDateTime = (ts?: number | null): string => {
  if (!ts) return 'N/A';
  const normalized = ts < 946684800000 ? ts * 1000 : ts;
  return new Date(normalized).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * ONE source for terminal-type wording — `FORM_FACTOR_LABEL` in shared-types.
 *
 * This was a local `switch`, and there were five of them. They had already
 * drifted: the two in platform-app did not handle `android_dual_screen` at all,
 * so a K10 showed a tenant the raw string `android_dual_screen`.
 */
const typeLabel = (t: string) => formFactorLabel(t);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PosTerminalDetailsTab: React.FC<Props> = ({ terminal, isOnline }) => {
  const [events, setEvents] = useState<TerminalEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const resp = await api.posTerminals.events(terminal.terminalId);
      setEvents(resp?.events || []);
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [terminal.terminalId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const usableCode = usableTerminalCode(terminal);
  const handleCopyCode = () => {
    if (!usableCode) return;
    navigator.clipboard.writeText(usableCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  // Online terminals are created already-active (no PROVISION# row, no device
  // to activate) — never treat them as pending/expired regardless of the
  // `status` field's raw value.
  const isOnlineSalesTerminal = terminal.terminalType === 'online_sales';
  // What the till reports — live connection + register row — not the provision row.
  const display = terminalDisplayStatus(terminal, isOnline);
  const conn: { tone: DisplayTone; title: string; subtitle: string } = isOnlineSalesTerminal
    ? { tone: 'emerald', title: 'Online — Active', subtitle: 'Online sales terminal — always active, no device activation required' }
    : !display.registered
      ? {
          tone: display.tone,
          title: display.label === 'Expired' ? 'Provision Code Expired' : 'Pending Activation',
          subtitle: 'Waiting for terminal to activate with provision code',
        }
      : {
          tone: isOnline ? 'emerald' : 'red',
          title: `${isOnline ? 'Online — Connected' : 'Offline — Disconnected'} · ${display.label}`,
          subtitle: display.hint
            || (terminal.lastSeenAt ? `Last seen ${formatDateTime(terminal.lastSeenAt)}` : 'No activity recorded yet'),
        };

  return (
    <div className="space-y-6">
      {/* Terminal Information */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <h3 className="text-stone-800 font-semibold text-sm mb-4">Terminal Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0 mt-0.5">
              <Hash size={14} className="text-stone-500" />
            </div>
            <div>
              <p className="text-[10px] text-stone-400 uppercase tracking-wider">Terminal ID</p>
              <p className="text-stone-700 text-sm font-mono mt-0.5">{terminal.terminalId}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0 mt-0.5">
              <Monitor size={14} className="text-stone-500" />
            </div>
            <div>
              <p className="text-[10px] text-stone-400 uppercase tracking-wider">Terminal Type</p>
              <p className="text-stone-700 text-sm mt-0.5">{typeLabel(terminal.terminalType)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0 mt-0.5">
              <MapPin size={14} className="text-stone-500" />
            </div>
            <div>
              <p className="text-[10px] text-stone-400 uppercase tracking-wider">Location</p>
              <p className="text-stone-700 text-sm mt-0.5">{terminal.locationName || terminal.locationId}</p>
            </div>
          </div>

          {/* Online terminals never have a provision code (created active, no
              device to activate) — skip this field for them. */}
          {!isOnlineSalesTerminal && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0 mt-0.5">
                <Key size={14} className="text-stone-500" />
              </div>
              <div>
                <p className="text-[10px] text-stone-400 uppercase tracking-wider">Provision Code</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className={`text-sm font-mono ${usableCode ? 'text-stone-700' : 'text-stone-400'}`}>{terminal.provisionCode || '—'}</p>
                  {usableCode && (
                    <button
                      onClick={handleCopyCode}
                      className="p-1 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors"
                      title="Copy code"
                    >
                      {copiedCode ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                    </button>
                  )}
                </div>
                {!usableCode && terminal.provisionCode && (
                  <p className="text-[11px] text-stone-400 mt-0.5">
                    {terminal.codeExpired ? 'Expired' : 'Already used'} — use &quot;Generate new code&quot; on the Terminals list for a fresh one.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0 mt-0.5">
              <Calendar size={14} className="text-stone-500" />
            </div>
            <div>
              <p className="text-[10px] text-stone-400 uppercase tracking-wider">Created</p>
              <p className="text-stone-700 text-sm mt-0.5">{formatDateTime(terminal.createdAt)}</p>
            </div>
          </div>

          {terminal.status === 'activated' && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0 mt-0.5">
                <Wifi size={14} className="text-stone-500" />
              </div>
              <div>
                <p className="text-[10px] text-stone-400 uppercase tracking-wider">Activated</p>
                <p className="text-stone-700 text-sm mt-0.5">{formatDateTime(terminal.activatedAt)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Connection Status */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <h3 className="text-stone-800 font-semibold text-sm mb-4">Connection Status</h3>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${ICON_BG[conn.tone]}`}>
            <Activity size={18} className={ICON_TEXT[conn.tone]} />
          </div>
          <div>
            <p className={`text-sm font-medium ${TITLE_TEXT[conn.tone]}`}>{conn.title}</p>
            <p className="text-stone-500 text-xs mt-0.5">{conn.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Login / Logout History */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <h3 className="text-stone-800 font-semibold text-sm mb-4">Login / Logout History</h3>
        {eventsLoading ? (
          <RubyLoader variant="section" label="Loading events..." className="py-4" />
        ) : events.length === 0 ? (
          <div className="text-center py-6">
            <Clock size={24} className="mx-auto mb-2 text-stone-300" />
            <p className="text-stone-500 text-sm">No activity recorded yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {events.map((ev, i) => (
              <div
                key={i}
                className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-stone-50 border border-stone-100"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  ev.type === 'login' ? 'bg-emerald-100' : 'bg-red-100'
                }`}>
                  {ev.type === 'login' ? (
                    <LogIn size={14} className="text-emerald-600" />
                  ) : (
                    <LogOut size={14} className="text-red-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-stone-800 text-sm font-medium">{ev.employeeName}</p>
                  <p className="text-stone-500 text-xs capitalize">{ev.type}</p>
                </div>
                <span className="text-stone-400 text-xs shrink-0">
                  {formatDateTime(ev.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PosTerminalDetailsTab;
